import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { RotateCcw } from 'lucide-react';
import { SlideStage } from '../../components/deck/SlideStage';
import { EmergencyPhase, VexuEmergencyShowcase } from '../../components/deck/VexuEmergencyShowcase';
import { emergencyStages } from '../../data/deckSlide6';
const VEXU_LOGO = 'https://cdn.magicpatterns.com/uploads/3A8d459aXLumR8KKFCQtrD/vexu_logo.svg';
const ease = [0.22, 0.61, 0.36, 1] as const;
const T = {
  headline: 0.2,
  support: 0.9,
  phone: 1.2,
  stages: 2.0
};
export function EmergencySlide() {
  const [runKey, setRunKey] = useState(0);
  const [phase, setPhase] = useState<EmergencyPhase>('idle');
  const [finished, setFinished] = useState(false);
  const replay = () => {
    setFinished(false);
    setRunKey(key => key + 1);
  };
  return <SlideStage data-id="element-2247" data-mp="lbqsemb5kfpc">
      <div className="deck-type absolute inset-0 overflow-hidden bg-[#FAFCF9]" data-id="element-2248" data-mp="lfobj7xqd798">
        <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse 90% 70% at 8% -18%, rgba(0,200,83,0.06) 0%, rgba(250,252,249,0) 60%), radial-gradient(ellipse 55% 60% at 74% 45%, rgba(180,84,58,0.06) 0%, rgba(250,252,249,0) 68%), linear-gradient(180deg, #FBFDFA 0%, #F5F8F3 55%, #EFF3ED 100%)'
      }} data-id="element-2249" data-mp="ge8stmj0zcu0" />

        {/* Brand header */}
        <motion.div className="absolute flex items-center gap-3" style={{
        left: 112,
        top: 56
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2250" data-mp="1u37ru0kyyv6a">
          <img src={VEXU_LOGO} alt="" className="w-7 h-7 object-contain" data-id="element-2251" data-mp="16fg5s59mn3rt" />
          <span className="text-[16px] font-bold tracking-tight text-[#0B1712]" data-id="element-2252" data-mp="1b66bxkpvnank">
            Vexu.ai
          </span>
        </motion.div>
        <motion.span className="absolute text-[11px] font-semibold uppercase tracking-[0.28em] text-[#8A998F]" style={{
        right: 112,
        top: 62
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2253" data-mp="dq0tlc6874ub">
          06 · Emergency Detection
        </motion.span>

        {/* LEFT — the claim */}
        <h1 className="absolute text-[46px] font-semibold leading-[1.16] tracking-[-0.02em] text-[#0B1712]" style={{
        left: 112,
        top: 190,
        width: 500
      }} data-id="element-2254" data-mp="kfimt3x255s9">
          {['When it matters,', 'VEXU knows.'].map((line, i) => <motion.span key={line} className="block" initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: T.headline + i * 0.16,
          ease
        }} data-id="element-2255" data-mp="3ny25pftuizae" data-mpi={i}>
              {line}
            </motion.span>)}
        </h1>

        <motion.p className="absolute text-[18px] leading-relaxed text-[#5A6B60]" style={{
        left: 112,
        top: 326,
        width: 460
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 1,
        delay: T.support,
        ease
      }} data-id="element-2256" data-mp="1d3es2x0p060n">
          VEXU can detect urgency, alert you instantly, and keep the caller
          engaged while you decide what to do.
        </motion.p>

        <div className="absolute" style={{
        left: 112,
        top: 458,
        width: 440
      }} data-id="element-2257" data-mp="2h33ao7g0nhpa">
          {emergencyStages.map((stage, i) => {
          const active = phase === stage.id || phase === 'done' && stage.id === 'live';
          return <motion.div key={stage.id} className="border-t border-[#0B1712]/[0.09] py-5 pl-4 -ml-4 rounded-r-xl transition-colors duration-500" style={{
            backgroundColor: active ? 'rgba(0,200,83,0.07)' : 'rgba(0,200,83,0)'
          }} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: T.stages + i * 0.2,
            ease
          }} data-id="element-2258" data-mp="3drsvo6mxwqfh" data-mpi={i}>
                <p className={`text-[11px] font-semibold uppercase tracking-[0.24em] transition-colors duration-500 ${active ? 'text-vexu-700' : 'text-vexu-700/70'}`} data-id="element-2259" data-mp="28v07x2f6hzgz">
                  {stage.label}
                </p>
                <p className={`mt-2 text-[17px] transition-colors duration-500 ${active ? 'font-medium text-[#0B1712]' : 'text-[#33463B]'}`} data-id="element-2260" data-mp="1uupgny9yiiva">
                  {stage.detail}
                </p>
              </motion.div>;
        })}
        </div>

        <AnimatePresence data-id="element-2261" data-mp="2iseor350kq9t">
          {finished && <motion.button type="button" onClick={replay} initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.6,
          ease
        }} className="absolute inline-flex items-center gap-2 rounded-full border border-[#0B1712]/10 bg-white/70 px-4 py-2 text-[12.5px] font-medium text-[#5A6B60] backdrop-blur-sm transition-colors hover:text-[#0B1712]" style={{
          left: 112,
          top: 742
        }} data-id="element-2262" data-mp="2iqtu7dip97by">
              <RotateCcw className="h-3.5 w-3.5" data-id="element-2263" data-mp="3nrjmz7cx45ho" />
              Replay simulation
            </motion.button>}
        </AnimatePresence>

        {/* RIGHT — the real emergency flow */}
        <motion.div className="absolute" style={{
        left: 1060,
        top: 84
      }} initial={{
        opacity: 0,
        y: 24
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1.2,
        delay: T.phone,
        ease
      }} data-id="element-2264" data-mp="mln38022jqx">
          <VexuEmergencyShowcase scale={0.86} runKey={runKey} onPhaseChange={setPhase} onComplete={() => setFinished(true)} data-id="element-2265" data-mp="adq56gl12kpz" />
        </motion.div>
      </div>
    </SlideStage>;
}